import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, QrCode, CreditCard } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, brl, errMsg } from "@/lib/api";
import { Field, inputCls } from "@/components/landing/ProposalForm";

export const MethodPicker = ({ method, setMethod, mpEnabled }) => (
  <div className="grid gap-3 sm:grid-cols-2">
    {[
      { id: "pix", label: "Pix", desc: "Pague e envie o comprovante", Icon: QrCode, enabled: true },
      { id: "mercadopago", label: "Mercado Pago", desc: mpEnabled ? "Cartão, Pix ou saldo" : "Em breve", Icon: CreditCard, enabled: mpEnabled },
    ].map(({ id, label, desc, Icon, enabled }) => (
      <button type="button" key={id} disabled={!enabled} onClick={() => setMethod(id)} data-testid={`method-${id}`}
        className={`flex items-center gap-3 rounded-2xl border p-4 text-left transition-colors duration-300 disabled:cursor-not-allowed disabled:opacity-45 ${method === id ? "border-flash bg-flash/10" : "border-white/10 hover:border-white/30"}`}>
        <Icon className={`h-6 w-6 ${method === id ? "text-flash" : "text-haze"}`} />
        <span>
          <span className="block font-semibold">{label}</span>
          <span className="block text-xs text-haze">{desc}</span>
        </span>
      </button>
    ))}
  </div>
);

export const CheckoutDialog = ({ open, onOpenChange, event, photoIds, mpEnabled, onDone }) => {
  const nav = useNavigate();
  const [f, setF] = useState({ name: "", email: "", phone: "" });
  const [method, setMethod] = useState("pix");
  const [busy, setBusy] = useState(false);
  const total = (event?.price_per_photo_cents ?? 0) * photoIds.length;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/orders", { ...f, method, event_id: event.id, photo_ids: photoIds });
      onDone?.();
      if (data?.init_point) window.location.assign(data.init_point);
      else nav(`/pedido/${data.id}?t=${data.token}`);
    } catch (err) {
      toast.error(errMsg(err));
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg rounded-[1.5rem] border-white/10 bg-ink-2" data-testid="checkout-dialog" data-lenis-prevent>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Finalizar compra</DialogTitle>
          <DialogDescription className="text-haze">
            {photoIds.length} foto(s) · <span className="font-semibold text-flash">{brl(total)}</span>
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-2 grid gap-4">
          <Field label="Nome completo"><input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} data-testid="checkout-name-input" /></Field>
          <Field label="E-mail"><input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} data-testid="checkout-email-input" /></Field>
          <Field label="WhatsApp (opcional)"><input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className={inputCls} data-testid="checkout-phone-input" /></Field>
          <MethodPicker method={method} setMethod={setMethod} mpEnabled={mpEnabled} />
          <button disabled={busy} data-testid="checkout-submit-btn"
            className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-flash px-6 py-4 font-semibold text-ink transition-colors hover:bg-flash-2 disabled:opacity-60">
            {busy && <Loader2 className="h-5 w-5 animate-spin" />} {method === "pix" ? "Gerar pedido e pagar com Pix" : "Ir para o Mercado Pago"}
          </button>
          <p className="text-center text-xs text-haze">Guarde o link do pedido: é por ele que você baixa suas fotos.</p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
